/**
 * ThankYouDesigns — 감사장 디자인 목록 페이지
 *
 * 감사장(thank-you card) 디자인 템플릿을 그리드로 보여주고, 생성·삭제를 처리한다.
 * 카드를 클릭하면 디자인 에디터(`/wedding/thankyou-designs/:id`)로 이동한다.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Trash2, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useApiFetch } from '../hooks/useApiFetch'
import { useToast } from '../components/ToastContainer'
import { apiFetch } from '../lib/api'
import { Button } from '../ui/Button'
import { Toggle } from '../ui/Toggle'
import { Spinner } from '../ui/Spinner'
import { EmptyState } from '../ui/EmptyState'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { DEFAULT_HTML } from '../lib/thankyou-types'
import { renderThankYouPreview } from '../lib/thankyou-render'
import type { ThankYouDesign } from '../lib/thankyou-types'

export function ThankYouDesigns() {
  const navigate = useNavigate()
  const { addToast } = useToast()

  const { data, isLoading, refetch } = useApiFetch<ThankYouDesign[]>(
    '/api/admin/thankyou-designs?includeInactive=true',
  )

  // 드래그 정렬 optimistic 반영을 위해 서버 목록을 로컬 state로 복사한다.
  const [designs, setDesigns] = useState<ThankYouDesign[]>([])
  useEffect(() => {
    if (data) setDesigns(data)
  }, [data])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // 드래그 종료 → 로컬 즉시 재정렬 후 서버 저장, 실패 시 롤백
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = designs.findIndex((d) => d.id === active.id)
      const newIndex = designs.findIndex((d) => d.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const prev = designs
      const next = arrayMove(designs, oldIndex, newIndex)
      setDesigns(next)
      try {
        await apiFetch('/api/admin/thankyou-designs/reorder', {
          method: 'POST',
          body: JSON.stringify({ orderedIds: next.map((d) => d.id) }),
        })
      } catch (err) {
        setDesigns(prev)
        addToast({ type: 'error', message: err instanceof Error ? err.message : '순서 변경에 실패했습니다' })
      }
    },
    [designs, addToast],
  )

  const [creating, setCreating] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ThankYouDesign | null>(null)
  const [deleting, setDeleting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  // 활성/비활성 토글 — status만 PATCH
  const handleToggleStatus = useCallback(async (d: ThankYouDesign) => {
    const next = d.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    setTogglingId(d.id)
    try {
      await apiFetch(`/api/admin/thankyou-designs/${d.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      })
      addToast({ type: 'success', message: next === 'ACTIVE' ? '활성화했습니다' : '비활성화했습니다' })
      refetch()
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '상태 변경에 실패했습니다' })
    } finally {
      setTogglingId(null)
    }
  }, [addToast, refetch])

  // 새 디자인 생성 → 에디터로 이동
  const handleCreate = useCallback(async () => {
    setCreating(true)
    try {
      const created = await apiFetch<ThankYouDesign>('/api/admin/thankyou-designs', {
        method: 'POST',
        body: JSON.stringify({
          name: '새 디자인',
          status: 'INACTIVE',
          html: DEFAULT_HTML,
        }),
      })
      navigate(`/wedding/thankyou-designs/${created.id}`)
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '디자인 생성에 실패했습니다' })
    } finally {
      setCreating(false)
    }
  }, [navigate, addToast])

  // HTML/ZIP 임포트 → INACTIVE 디자인 생성 후 에디터로 이동
  const handleImport = useCallback(async (file: File) => {
    setImporting(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const created = await apiFetch<ThankYouDesign>('/api/admin/thankyou-designs/import', {
        method: 'POST',
        body: fd,
      })
      addToast({ type: 'success', message: '임포트했습니다 — placeholder를 확인하세요' })
      navigate(`/wedding/thankyou-designs/${created.id}`)
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '임포트에 실패했습니다' })
    } finally {
      setImporting(false)
    }
  }, [navigate, addToast])

  // 디자인 삭제
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/thankyou-designs/${deleteTarget.id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: '삭제되었습니다' })
      setDeleteTarget(null)
      refetch()
    } catch (err) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '삭제에 실패했습니다' })
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, addToast, refetch])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
        <Spinner />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>감사장 디자인</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input ref={fileRef} type="file" accept=".zip,.html,.htm" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImport(f); e.target.value = '' }} />
          <Button variant="secondary" loading={importing} onClick={() => fileRef.current?.click()}>HTML/ZIP 업로드</Button>
          <Button variant="primary" onClick={handleCreate} loading={creating}>
            새 디자인
          </Button>
        </div>
      </div>

      {designs.length === 0 ? (
        <EmptyState
          title="등록된 감사장 디자인이 없습니다"
          description="‘새 디자인’ 버튼을 눌러 첫 디자인을 만들어보세요"
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={designs.map((d) => d.id)} strategy={rectSortingStrategy}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '20px',
              }}
            >
              {designs.map((d) => (
                <SortableDesignCard key={d.id} design={d}>
                  {({ dragHandle }) => (
                    <>
                      <button
                        type="button"
                        onClick={() => navigate(`/wedding/thankyou-designs/${d.id}`)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: 0,
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          background: 'none',
                          cursor: 'pointer',
                        }}
                        aria-label={`${d.name} 디자인 편집`}
                      >
                        <DesignThumb design={d} />
                      </button>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                          {dragHandle}
                          <div style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.name}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <Toggle
                            size="sm"
                            checked={d.status === 'ACTIVE'}
                            disabled={togglingId !== null}
                            onChange={() => void handleToggleStatus(d)}
                            aria-label={`${d.name} ${d.status === 'ACTIVE' ? '비활성화' : '활성화'}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(d)}
                            title="삭제"
                            aria-label={`${d.name} 삭제`}
                          >
                            <Trash2 size={14} color="#ef4444" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </SortableDesignCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="디자인 삭제"
        message={deleteTarget ? `"${deleteTarget.name}" 디자인을 삭제하시겠습니까?` : ''}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}

/** 드래그 정렬 가능한 카드 래퍼 — 드래그 핸들만 따로 내려주고, 카드 본체 클릭은 보존한다. */
function SortableDesignCard({
  design,
  children,
}: {
  design: ThankYouDesign
  children: (slots: { dragHandle: React.ReactNode }) => React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: design.id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  }
  const dragHandle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      aria-label="드래그하여 순서 변경"
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '2px',
        border: 'none',
        background: 'none',
        cursor: 'grab',
        color: '#9ca3af',
        flexShrink: 0,
      }}
    >
      <GripVertical size={16} />
    </button>
  )
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandle })}
    </div>
  )
}

/** 목록 카드용 미니 프리뷰 — thumbnailUrl 있으면 이미지, 없으면 iframe 미니 렌더(360px 기준 scale) */
function DesignThumb({ design }: { design: ThankYouDesign }) {
  const ref = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)
  const [srcDoc, setSrcDoc] = useState('')
  const aspect = design.aspectRatio.replace('/', ' / ')

  useEffect(() => {
    if (design.thumbnailUrl) return
    const el = ref.current
    if (!el) return
    const update = () => setScale(el.clientWidth / 360)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [design.thumbnailUrl])

  useEffect(() => {
    if (design.thumbnailUrl) return
    let alive = true
    renderThankYouPreview(design.html).then((html) => { if (alive) setSrcDoc(html) }).catch(() => {})
    return () => { alive = false }
  }, [design.thumbnailUrl, design.html])

  if (design.thumbnailUrl) {
    return <img src={design.thumbnailUrl} alt="" style={{ width: '100%', aspectRatio: aspect, objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
  }
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', aspectRatio: aspect, overflow: 'hidden', pointerEvents: 'none' }}>
      {scale > 0 && srcDoc && (
        <iframe
          srcDoc={srcDoc} sandbox="" tabIndex={-1} title=""
          style={{ width: 360, height: `calc(360px / (${aspect}))`, border: 0, transformOrigin: 'top left', transform: `scale(${scale})` }}
        />
      )}
    </div>
  )
}
